import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Send, CheckCircle2, ExternalLink, Settings, Building2, Lock, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { CardSkeleton } from "../components/Skeletons";
import HelpIcon from "../components/help/HelpIcon";

export default function PPFConfigPage() {
  const { axiosAuth } = useAuth();
  // useMemo pour eviter la recreation de l'axios instance a chaque render
  // (sinon useCallback(load) recalcule -> useEffect refire en boucle)
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [operators, setOperators] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("config");

  const load = useCallback(async () => {
    setLoading(true);
    // Promise.allSettled : un 429 sur un endpoint ne bloque pas le rendu de l'autre
    const [opsRes, cfgRes] = await Promise.allSettled([
      api.get("/ppf/operators"),
      api.get("/ppf/config"),
    ]);
    if (opsRes.status === "fulfilled") {
      setOperators(opsRes.value.data || []);
    } else {
      const status = opsRes.reason?.response?.status;
      if (status !== 429) {
        toast.error(opsRes.reason?.response?.data?.detail || "Erreur chargement operateurs");
      }
      setOperators([]);
    }
    if (cfgRes.status === "fulfilled") {
      setConfig(cfgRes.value.data);
    } else {
      const status = cfgRes.reason?.response?.status;
      if (status !== 429) {
        toast.error(cfgRes.reason?.response?.data?.detail || "Erreur chargement config");
      }
      setConfig(null);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6"><CardSkeleton /><CardSkeleton /></div>
  );

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="ppf-config-page">
      <div className="flex items-start gap-2">
        <PageHeader
          icon={<Send />}
          title="Facturation electronique B2B (PPF)"
          subtitle="Configurez votre operateur PDP (Plateforme de Dematerialisation Partenaire) pour la transmission des factures conformement a la reglementation francaise (obligatoire septembre 2026)."
        />
        <div className="pt-3"><HelpIcon topic="ppf_factur_x" size="md" /></div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#1a1625] border border-white/10 p-1 inline-flex">
          <TabsTrigger value="config" data-testid="ppf-tab-config" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"><Settings className="w-4 h-4" /> Configuration</TabsTrigger>
          <TabsTrigger value="operators" data-testid="ppf-tab-operators" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"><Building2 className="w-4 h-4" /> Catalogue operateurs</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="mt-6">
          <ConfigSection api={api} operators={operators} config={config} onChange={load} />
        </TabsContent>

        <TabsContent value="operators" className="mt-6">
          <OperatorsCatalog operators={operators} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConfigSection({ api, operators, config, onChange }) {
  const [form, setForm] = useState({
    provider: config?.provider || "",
    base_url: config?.base_url || "",
    client_id: config?.client_id || "",
    client_secret: "",
    tenant_pdp_id: config?.tenant_pdp_id || "",
    enabled: config?.enabled || false,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    if (!form.provider) { toast.error("Selectionnez un operateur"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_secret) delete payload.client_secret; // ne pas ecraser le secret existant
      await api.put("/ppf/config", payload);
      toast.success("Configuration enregistree");
      setForm(f => ({ ...f, client_secret: "" }));
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/ppf/test-connection");
      if (data.ok) toast.success(data.message || "Connexion OK");
      else toast.warning(data.message || "Echec connexion");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setTesting(false);
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#14101c] border border-white/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-[#f7c935]" /> Configuration PDP</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-slate-400 mb-1 block">Operateur PDP *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {operators.map(op => (
                <button
                  key={op.code}
                  onClick={() => setForm(f => ({ ...f, provider: op.code }))}
                  data-testid={`ppf-pick-${op.code}`}
                  className={`text-left p-3 border rounded-lg transition-all ${form.provider === op.code ? "border-[#f7c935] bg-[#f7c935]/5" : "border-white/10 bg-[#1a1625] hover:border-white/20"}`}
                >
                  <div className="font-semibold text-white text-sm">{op.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{op.speciality}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">URL API operateur</label>
            <Input data-testid="ppf-base-url" value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))} placeholder="https://api.operator.com" className="bg-[#1a1625] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tenant PDP ID</label>
            <Input value={form.tenant_pdp_id} onChange={e => setForm(f => ({ ...f, tenant_pdp_id: e.target.value }))} placeholder="ID de votre compte chez l'operateur" className="bg-[#1a1625] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Client ID</label>
            <Input data-testid="ppf-client-id" value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} className="bg-[#1a1625] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">
              Client Secret {config?.has_secret && <span className="text-emerald-400 ml-2">(deja configure — laisser vide pour conserver)</span>}
            </label>
            <Input data-testid="ppf-client-secret" type="password" value={form.client_secret} onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} placeholder="••••••••" className="bg-[#1a1625] border-white/10" />
          </div>

          <div className="md:col-span-2 flex items-center gap-3 bg-[#1a1625] rounded-lg p-3 border border-white/5">
            <Switch data-testid="ppf-enabled" checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} />
            <div className="flex-1">
              <div className="text-sm font-semibold text-white">Activer la transmission au PDP</div>
              <div className="text-xs text-slate-500">Quand active, les factures generees passent automatiquement par l'operateur configure.</div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Button onClick={save} disabled={saving} data-testid="ppf-save-btn" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
          <Button onClick={testConnection} disabled={testing || !config?.provider} variant="outline" data-testid="ppf-test-btn" className="border-white/10 text-slate-300 hover:text-white">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Tester la connexion
          </Button>
          {config?.last_test_at && (
            <span className="text-xs text-slate-500">
              Dernier test : {(config.last_test_at || "").slice(0, 16).replace("T", " ")} ·
              {config.last_test_ok ? <span className="text-emerald-400 ml-1">OK</span> : <span className="text-red-400 ml-1">Echec</span>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function OperatorsCatalog({ operators }) {
  return (
    <div className="space-y-4" data-testid="operators-catalog">
      <p className="text-sm text-slate-400">Choisissez l'operateur PDP qui correspond le mieux a votre profil. Tous sont agrees PDP-PA et conformes Factur-X.</p>
      {operators.map(op => (
        <div key={op.code} className="bg-[#14101c] border border-white/10 rounded-xl p-5" data-testid={`op-card-${op.code}`}>
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-1">{op.name}</h3>
              <p className="text-sm text-slate-400">{op.speciality}</p>
              <p className="text-xs text-[#f7c935] mt-1">{op.pricing_indication}</p>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {op.compliance.map(c => <Badge key={c} variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">{c}</Badge>)}
            </div>
          </div>

          <details className="bg-[#1a1625] rounded-lg p-3 border border-white/5">
            <summary className="cursor-pointer text-sm font-semibold text-white">Etapes d'onboarding</summary>
            <ol className="mt-3 space-y-1.5 text-xs text-slate-400">
              {op.onboarding_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#f7c935] font-mono flex-shrink-0">{i + 1}.</span>
                  <span>{step.replace(/^\d+\.\s*/, "")}</span>
                </li>
              ))}
            </ol>
            {op.api_doc_url && (
              <a href={op.api_doc_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#f7c935] hover:underline mt-3">
                Documentation API <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </details>
        </div>
      ))}
    </div>
  );
}
